import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactElement } from 'react';

import { Notice } from '@/components/admin/Notice';
import {
  fetchMyResponse,
  saveDraft,
  submitResponse,
  uploadAnswerFile,
  type FileAnswerValue,
  type Question,
  type QuestionCondition,
  type SurveyAnswer,
} from '@/features/surveys/api';
import { ApiError } from '@/lib/api/http';
import { formatDateTime } from '@/lib/datetime';

function isConditionMet(condition: QuestionCondition, answers: Record<string, unknown>): boolean {
  const actual = answers[condition.question_id];
  const expected = condition.value;
  if (condition.operator === 'eq') return actual === expected;
  if (condition.operator === 'neq') return actual !== expected;
  const expectedList = Array.isArray(expected) ? expected : [expected];
  if (condition.operator === 'in') return expectedList.includes(actual);
  if (condition.operator === 'not_in') return !expectedList.includes(actual);
  return false;
}

function isVisible(question: Question, answers: Record<string, unknown>): boolean {
  if (!question.visible_if) return true;
  const { op, conditions } = question.visible_if;
  if (conditions.length === 0) return true;
  const results = conditions.map((c) => isConditionMet(c, answers));
  return op === 'all' ? results.every(Boolean) : results.some(Boolean);
}

/** 受講生本人の回答フォーム（設問9型・条件分岐・下書き自動保存・再提出 / 指示書 §12 M5）. */
export function SurveyAnswerForm({
  eventSurveyId,
  questions,
  deadlineAt,
}: {
  eventSurveyId: string;
  questions: Question[];
  deadlineAt: string | null;
}): ReactElement {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['survey-response', eventSurveyId],
    queryFn: () => fetchMyResponse(eventSurveyId),
  });

  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (query.data) {
      const initial: Record<string, unknown> = {};
      for (const a of query.data.answers) initial[a.question_id] = a.value;
      setAnswers(initial);
    }
  }, [query.data]);

  if (query.isPending) return <p className="text-wptext-muted">読み込み中…</p>;
  if (query.isError) return <p className="text-wpnotice-error">読み込みに失敗しました。</p>;

  const response = query.data;
  const visibleQuestions = questions.filter((q) => isVisible(q, answers));

  function toAnswerList(): SurveyAnswer[] {
    return visibleQuestions
      .filter((q) => answers[q.id] !== undefined)
      .map((q) => ({ question_id: q.id, value: answers[q.id] }));
  }

  async function onSaveDraft(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await saveDraft(eventSurveyId, toAnswerList());
      await queryClient.invalidateQueries({ queryKey: ['survey-response', eventSurveyId] });
      setNotice('下書きを保存しました。');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '保存に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onSubmit(): Promise<void> {
    setBusy(true);
    setError(null);
    setFieldErrors({});
    try {
      const updated = await submitResponse(eventSurveyId, toAnswerList());
      await queryClient.invalidateQueries({ queryKey: ['survey-response', eventSurveyId] });
      await queryClient.invalidateQueries({ queryKey: ['event-surveys'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setNotice(updated.status === 'resubmitted' ? '再提出しました。' : '提出しました。');
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFieldErrors(err.fieldErrors ?? {});
      } else {
        setError('提出に失敗しました。');
      }
    } finally {
      setBusy(false);
    }
  }

  async function onUploadFile(question: Question, file: File): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const value = await uploadAnswerFile(eventSurveyId, question.id, file);
      setAnswers((prev) => ({ ...prev, [question.id]: value }));
      setNotice('ファイルをアップロードしました。提出ボタンで確定してください。');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'アップロードに失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="mb-1 flex flex-wrap items-center gap-3 text-wptext-muted">
        <span>状態: {STATUS_LABELS[response.status]}</span>
        {deadlineAt && <span>締切: {formatDateTime(deadlineAt)}</span>}
        {response.is_overdue && <span className="text-wpnotice-error">期限超過</span>}
        {response.draft_saved_at && (
          <span>途中保存: {formatDateTime(response.draft_saved_at)}</span>
        )}
      </div>
      <p className="mb-3 text-wptext-muted">
        <span className="text-wpnotice-error">*</span> が付いている設問は入力が必要です。
      </p>

      {notice && (
        <Notice kind="success" onDismiss={() => setNotice(null)}>
          {notice}
        </Notice>
      )}
      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}

      <div className="flex flex-col gap-5">
        {visibleQuestions.map((question, index) => (
          <QuestionField
            key={question.id}
            question={question}
            index={index + 1}
            value={answers[question.id]}
            error={fieldErrors[question.id]?.[0]}
            onChange={(value) => setAnswers((prev) => ({ ...prev, [question.id]: value }))}
            onUploadFile={(file) => void onUploadFile(question, file)}
          />
        ))}
      </div>

      <div className="mt-6 rounded-sm border border-wpborder bg-wpcanvas/60 px-4 py-4">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => void onSaveDraft()}
            className="btn-secondary"
          >
            途中まで保存する
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void onSubmit()}
            className="rounded-sm border border-transparent bg-wpaccent px-4 py-2.5 text-base font-semibold text-white hover:bg-wpaccent-hover disabled:opacity-50"
          >
            {response.status === 'draft' ? '提出する' : '再提出する'}
          </button>
        </div>
        <p className="mt-3 text-wptext-muted leading-relaxed">
          <strong className="text-wptext-strong">「提出する」を押すまで事務局には届きません。</strong>
          「途中まで保存する」は入力内容を残しておくだけです。提出後も締切までは内容を直して再提出できます。
        </p>
      </div>
    </div>
  );
}

const STATUS_LABELS: Record<string, string> = {
  // 「下書き」だけだと提出済みと区別がつきにくいため、未提出であることを明示する（D-083）
  draft: 'まだ提出していません',
  submitted: '提出済み',
  resubmitted: '再提出済み',
};

function QuestionField({
  question,
  index,
  value,
  error,
  onChange,
  onUploadFile,
}: {
  question: Question;
  index: number;
  value: unknown;
  error: string | undefined;
  onChange: (value: unknown) => void;
  onUploadFile: (file: File) => void;
}): ReactElement {
  if (question.type === 'description') {
    return (
      <p className="rounded-sm border border-wpborder bg-wpcanvas px-4 py-3 text-wptext-strong leading-relaxed">
        {question.label}
      </p>
    );
  }

  return (
    <div className="rounded-sm border border-wpborder bg-white px-4 py-4 shadow-sm">
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-wptext-muted">
        設問 {index}
      </p>
      <label className="mb-2 block text-base font-semibold text-wptext-strong leading-snug">
        {question.label}
        {question.required && <span className="ml-1 text-wpnotice-error">*</span>}
      </label>
      <QuestionInput question={question} value={value} onChange={onChange} onUploadFile={onUploadFile} />
      {error && <p className="mt-2 text-wpnotice-error">{error}</p>}
    </div>
  );
}

function QuestionInput({
  question,
  value,
  onChange,
  onUploadFile,
}: {
  question: Question;
  value: unknown;
  onChange: (value: unknown) => void;
  onUploadFile: (file: File) => void;
}): ReactElement {
  switch (question.type) {
    case 'short_text':
      return (
        <input
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-sm border border-wpborder px-3 py-2"
        />
      );
    case 'long_text':
      return (
        <textarea
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          rows={5}
          className="w-full rounded-sm border border-wpborder px-3 py-2 leading-relaxed"
        />
      );
    case 'date':
      return (
        <input
          type="date"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          className="rounded-sm border border-wpborder px-3 py-2"
        />
      );
    case 'datetime':
      return (
        <input
          type="datetime-local"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          className="rounded-sm border border-wpborder px-3 py-2"
        />
      );
    case 'scale':
      return (
        <div className="flex flex-wrap gap-2">
          {[1, 2, 3, 4, 5].map((n) => (
            <label
              key={n}
              className={`flex min-w-[3rem] cursor-pointer items-center justify-center rounded-sm border px-3 py-2 ${
                value === n
                  ? 'border-wpaccent bg-wpaccent/10 font-semibold text-wpaccent'
                  : 'border-wpborder hover:bg-wpcanvas'
              }`}
            >
              <input
                type="radio"
                className="sr-only"
                checked={value === n}
                onChange={() => onChange(n)}
                name={question.id}
              />
              {n}
            </label>
          ))}
        </div>
      );
    case 'dropdown':
      return (
        <select
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          className="rounded-sm border border-wpborder px-3 py-2"
        >
          <option value="">選択してください</option>
          {question.options?.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    case 'single_choice':
      return (
        <div className="flex flex-col gap-2">
          {question.options?.map((opt) => (
            <label
              key={opt.value}
              className={`flex cursor-pointer items-start gap-3 rounded-sm border px-3 py-2.5 ${
                value === opt.value ? 'border-wpaccent bg-wpaccent/5' : 'border-wpborder hover:bg-wpcanvas'
              }`}
            >
              <input
                type="radio"
                className="mt-1"
                name={question.id}
                checked={value === opt.value}
                onChange={() => onChange(opt.value)}
              />
              <span className="leading-relaxed">{opt.label}</span>
            </label>
          ))}
        </div>
      );
    case 'multi_choice': {
      const selected = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div className="flex flex-col gap-2">
          {question.options?.map((opt) => (
            <label
              key={opt.value}
              className={`flex cursor-pointer items-start gap-3 rounded-sm border px-3 py-2.5 ${
                selected.includes(opt.value)
                  ? 'border-wpaccent bg-wpaccent/5'
                  : 'border-wpborder hover:bg-wpcanvas'
              }`}
            >
              <input
                type="checkbox"
                className="mt-1"
                checked={selected.includes(opt.value)}
                onChange={(e) =>
                  onChange(
                    e.target.checked
                      ? [...selected, opt.value]
                      : selected.filter((v) => v !== opt.value),
                  )
                }
              />
              <span className="leading-relaxed">{opt.label}</span>
            </label>
          ))}
        </div>
      );
    }
    case 'file': {
      const fileValue = value as FileAnswerValue | undefined;
      return (
        <div>
          {fileValue && (
            <p className="mb-1 text-wptext-muted">アップロード済み: {fileValue.filename}</p>
          )}
          <input
            type="file"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onUploadFile(file);
            }}
          />
        </div>
      );
    }
    case 'matrix': {
      const answers = (value && typeof value === 'object' ? value : {}) as Record<string, string>;
      function setRow(rowValue: string, colValue: string): void {
        onChange({ ...answers, [rowValue]: colValue });
      }
      return (
        <div className="overflow-x-auto">
          <table className="border-collapse text-left">
            <thead>
              <tr>
                <th className="px-2 py-1" />
                {question.options?.map((col) => (
                  <th key={col.value} className="px-2 py-1 text-wptext-muted">
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {question.rows?.map((row) => (
                <tr key={row.value}>
                  <td className="px-2 py-1 text-wptext-strong">{row.label}</td>
                  {question.options?.map((col) => (
                    <td key={col.value} className="px-2 py-1 text-center">
                      <input
                        type="radio"
                        name={`${question.id}-${row.value}`}
                        checked={answers[row.value] === col.value}
                        onChange={() => setRow(row.value, col.value)}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    default:
      return <p className="text-wptext-muted">未対応の設問形式です。</p>;
  }
}
